import { zodResolver } from '@hookform/resolvers/zod';
import { AppError } from '@repo/error';
import { errorMessage, t, validationError } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { UserRole } from '@repo/shared/types';
import { Badge } from '@repo/ui/badge';
import { Button } from '@repo/ui/button';
import { Card, CardContent } from '@repo/ui/card';
import { Stepper, StepperProvider, useStep } from '@repo/ui/stepper';
import { MailIcon, ShieldIcon, UserCheckIcon } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import z from 'zod';
import {
  CreateOfficerInformationForm,
  ZCreateOfficerInformationFormSchema,
} from '@/components/forms/create-officer-information-form';
import {
  CreateOfficerRoleForm,
  OFFICER_ROLE_LABELS,
} from '@/components/forms/create-officer-role-form';
import { trpc } from '@/providers/trpc';

export const ZCreateOfficerRoleFormSchema = z.object({
  role: z.enum([
    UserRole.ORG_MANAGER,
    UserRole.ORG_OFFICER,
    UserRole.ORG_KYC_OFFICER,
    UserRole.ORG_KYB_OFFICER,
  ]),
});

export const CreateOfficerPage = () => {
  useLocale();

  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);

  const infoForm = useForm({
    defaultValues: { firstName: '', middleName: '', lastName: '', phone: '', email: '' },
    mode: 'onBlur',
    resolver: zodResolver(ZCreateOfficerInformationFormSchema, { error: validationError }),
  });

  const roleForm = useForm({
    defaultValues: { role: UserRole.ORG_OFFICER },
    resolver: zodResolver(ZCreateOfficerRoleFormSchema, { error: validationError }),
  });

  const inviteOfficerMutation = trpc.organization.inviteOfficer.useMutation({
    onSuccess: () => {
      setCurrentStep(4);
    },
    onError: (err) => {
      const error = AppError.parseError(err);
      toast.error(t('auth.failedToSendInvitation'), {
        description: errorMessage(AppError.parse(error)),
      });
    },
  });

  const handleSendEmail = () => {
    const officer = infoForm.getValues();
    const { role } = roleForm.getValues();
    inviteOfficerMutation.mutate({
      email: officer.email,
      firstName: officer.firstName,
      middleName: officer.middleName,
      lastName: officer.lastName,
      phone: officer.phone,
      role,
    });
  };

  return (
    <div className="flex-1 space-y-6 p-4 pt-6 md:p-8">
      <div className="mx-auto w-full max-w-2xl">
        <h1 className="font-bold text-3xl tracking-tight">{t('workspace.inviteOfficer')}</h1>
        <p className="mt-1 text-muted-foreground">
          {t('auth.inviteANewOfficerAndSetTheirRoleAnd')}{' '}
        </p>
      </div>
      <StepperProvider currentStep={currentStep} setCurrentStep={setCurrentStep}>
        <StepFrame>
          <CreateOfficerInformationForm form={infoForm} onNext={() => setCurrentStep(2)} />
        </StepFrame>
        <StepFrame>
          <CreateOfficerRoleForm form={roleForm} onNext={() => setCurrentStep(3)} />
        </StepFrame>
        <StepFrame>
          <ReviewStep
            isPending={inviteOfficerMutation.isPending}
            officerEmail={infoForm.getValues().email}
            onCancel={() => void navigate('../officers')}
            onSendEmail={handleSendEmail}
            roleLabel={OFFICER_ROLE_LABELS[roleForm.getValues().role]}
          />
        </StepFrame>
        <StepFrame>
          <ConfirmationStep
            officerEmail={infoForm.getValues().email}
            onExit={() => void navigate('../officers')}
          />
        </StepFrame>
      </StepperProvider>
    </div>
  );
};

const StepFrame = ({ children }: { children: React.ReactNode }) => {
  useLocale();

  const { currentStep } = useStep();
  const config = STEPS_CONFIG[currentStep - 1]!;

  return (
    <div className="mx-auto w-full max-w-2xl">
      <div className="mb-8 space-y-1">
        <Stepper className="mb-6" />
        <h2 className="font-semibold text-xl tracking-tight">{config.title}</h2>
        {config.description && (
          <p className="text-muted-foreground text-sm">{config.description}</p>
        )}
      </div>
      {children}
    </div>
  );
};

const ReviewStep = ({
  officerEmail,
  roleLabel,
  onCancel,
  onSendEmail,
  isPending,
}: {
  officerEmail: string;
  roleLabel: string;
  onCancel: () => void;
  onSendEmail: () => void;
  isPending: boolean;
}) => {
  useLocale();

  const { previousStep } = useStep();

  const emailSubject = t('invitation.subject');
  const emailBody = t('invitation.officerBody', { role: roleLabel });

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="space-y-3 pt-5">
          <div className="flex items-center gap-2 text-sm">
            <MailIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="text-muted-foreground">{t('common.recipient')}</span>
            <span className="font-medium">{officerEmail}</span>
          </div>
          <div className="space-y-2 border-t pt-3">
            <div>
              <p className="mb-1 text-muted-foreground text-xs uppercase tracking-wide">
                {t('auth.subject')}
              </p>
              <p className="font-medium text-sm">{emailSubject}</p>
            </div>
            <div>
              <p className="mb-1 text-muted-foreground text-xs uppercase tracking-wide">
                {t('auth.body')}
              </p>
              <div className="whitespace-pre-wrap rounded-md bg-muted/40 p-3 font-sans text-foreground/80 text-sm leading-relaxed">
                {emailBody}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between pt-6">
        <Button onClick={onCancel} type="button" variant="outline">
          {t('reports.cancel')}{' '}
        </Button>
        <Button loading={isPending} onClick={onSendEmail} type="button">
          <MailIcon className="me-2 h-4 w-4" />
          {t('auth.sendEmail')}{' '}
        </Button>
      </div>
    </div>
  );
};

const ConfirmationStep = ({
  officerEmail,
  onExit,
}: {
  officerEmail: string;
  onExit: () => void;
}) => {
  useLocale();

  return (
    <div className="flex flex-col items-center space-y-4 py-8 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
        <UserCheckIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
      </div>
      <div className="space-y-2">
        <h3 className="font-semibold text-lg">{t('auth.invitationSentSuccessfully')}</h3>
        <p className="max-w-md text-muted-foreground">
          {t('auth.theInvitationEmailWasSuccessfullySentTo')}{' '}
          <span className="font-medium text-foreground">{officerEmail}</span>{' '}
          {t('copy.officerActivation')}{' '}
        </p>
      </div>
      <div className="mt-2 flex items-center gap-2">
        <Badge variant="secondary">
          <ShieldIcon className="me-1.5 h-3.5 w-3.5" />
          {t('auth.pendingActivation')}{' '}
        </Badge>
      </div>
      <div className="flex w-full justify-end pt-4">
        <Button onClick={onExit} type="button">
          {t('auth.exit')}{' '}
        </Button>
      </div>
    </div>
  );
};

const STEPS_CONFIG = [
  {
    number: 1,
    get title() {
      return t('auth.enterTheOfficerSPersonalAndContactInformation');
    },
    description: '',
  },
  {
    number: 2,
    get title() {
      return t('auth.setTheOfficerSRoleAndPermissions');
    },
    get description() {
      return t('auth.forEachRoleADefaultSetOfPermissionsIs');
    },
  },
  {
    number: 3,
    get title() {
      return t('auth.sendAnInvitationEmailToTheOfficer');
    },
    get description() {
      return t('auth.pleaseReviewAndConfirmTheEmailContent');
    },
  },
  {
    number: 4,
    get title() {
      return t('auth.activatingTheOfficerSAccount');
    },
    get description() {
      return t('auth.theOfficerSAccountWillBeActivatedOnceThey');
    },
  },
];
